import type { ComponentType } from 'react'
import {
  OnboardingUserType,
  type KnownOnboardingStepId,
  type OnboardingStateResponse,
} from '@super/shared-types'
import { SetupStep } from './steps/SetupStep'
import { CompanyStep } from './steps/CompanyStep'
import { NumberStep } from './steps/NumberStep'
import { IntegrationsStep } from './steps/IntegrationsStep'
import { IntegrationConnectorStep } from './steps/IntegrationConnectorStep'
import { PlanStep } from './steps/PlanStep'
import { BillingStep } from './steps/BillingStep'
import { RecapStep } from './steps/RecapStep'
import { DoneStep } from './steps/DoneStep'

export type OnboardingPath = OnboardingUserType | 'both'

// Ids for header-visible steps are the canonical KnownOnboardingStepId set;
// hidden sub-steps (connector loops) use extra ids that never leave the client.
export type OnboardingStepId = KnownOnboardingStepId | 'integrations-connector'

export interface OnboardingStep {
  id: OnboardingStepId
  label: string
  route: string
  component: ComponentType
  path: OnboardingPath
  countsTowardProgress: boolean
  isOptional?: boolean
  isVisibleFor?: (state: OnboardingStateResponse) => boolean
  canAdvance?: (state: OnboardingStateResponse) => boolean
}

export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'fork',
    label: 'SETUP',
    route: '/onboarding/setup',
    component: SetupStep,
    path: 'both',
    countsTowardProgress: false,
  },
  {
    id: 'company',
    label: 'COMPANY',
    route: '/onboarding/company',
    component: CompanyStep,
    path: 'both',
    countsTowardProgress: true,
  },
  {
    id: 'number',
    label: 'PHONE NUMBER',
    route: '/onboarding/number',
    component: NumberStep,
    path: 'both',
    countsTowardProgress: true,
  },
  {
    id: 'integrations',
    label: 'INTEGRATIONS',
    route: '/onboarding/integrations',
    component: IntegrationsStep,
    path: 'both',
    countsTowardProgress: true,
  },
  // Hidden sub-step: /onboarding/integrations/:type renders the shared
  // IntegrationConnector inside the onboarding shell. Header pill count stays
  // at 4 because countsTowardProgress is false. findStepByRoute still resolves
  // nested paths to the parent 'integrations' step via the startsWith fallback,
  // so the auto-persisted currentStepId stays 'integrations' throughout the
  // loop.
  {
    id: 'integrations-connector',
    label: '',
    route: '/onboarding/integrations/:type',
    component: IntegrationConnectorStep,
    path: 'both',
    countsTowardProgress: false,
    isVisibleFor: () => false,
  },
  {
    id: 'plan',
    label: 'PLAN',
    route: '/onboarding/plan',
    component: PlanStep,
    path: 'both',
    countsTowardProgress: true,
  },
  {
    id: 'billing',
    label: 'BILLING',
    route: '/onboarding/billing',
    component: BillingStep,
    path: 'both',
    countsTowardProgress: true,
  },
  {
    id: 'recap',
    label: 'RECAP',
    route: '/onboarding/recap',
    component: RecapStep,
    path: 'both',
    countsTowardProgress: false,
  },
  {
    id: 'done',
    label: 'DONE',
    route: '/onboarding/done',
    component: DoneStep,
    path: 'both',
    countsTowardProgress: false,
  },
]

// Hidden sub-step routes carry a param placeholder (e.g. ':type'). They're
// registered so the router can mount them, but the linear-flow helpers below
// (stepsFor, nextStep, previousStep, progress counter) treat them as invisible.
function isTopLevelStep(step: OnboardingStep): boolean {
  return !step.route.includes(':')
}

export function stepsFor(
  userType: OnboardingUserType | null,
): OnboardingStep[] {
  return ONBOARDING_STEPS.filter(
    step =>
      isTopLevelStep(step) &&
      (step.path === 'both' || (userType !== null && step.path === userType)),
  )
}

export function progressStepsFor(
  userType: OnboardingUserType | null,
): OnboardingStep[] {
  return stepsFor(userType).filter(step => step.countsTowardProgress)
}

export function findStepByRoute(pathname: string): OnboardingStep | null {
  return (
    ONBOARDING_STEPS.find(step => step.route === pathname) ??
    ONBOARDING_STEPS.find(step => pathname.startsWith(step.route + '/')) ??
    null
  )
}

export function findStepById(id: string): OnboardingStep | null {
  return ONBOARDING_STEPS.find(step => step.id === id) ?? null
}

export function nextStep(
  current: OnboardingStep,
  userType: OnboardingUserType | null,
): OnboardingStep | null {
  const steps = stepsFor(userType)
  const index = steps.findIndex(s => s.id === current.id)
  return index >= 0 && index < steps.length - 1 ? steps[index + 1] : null
}

export function previousStep(
  current: OnboardingStep,
  userType: OnboardingUserType | null,
): OnboardingStep | null {
  const steps = stepsFor(userType)
  const index = steps.findIndex(s => s.id === current.id)
  return index > 0 ? steps[index - 1] : null
}
